import { useEffect, useRef, useState } from 'react';
import { Languages, Sparkles } from 'lucide-react';
import { AI_TONES } from '@speles7172/ai-client/core';
import { useAppSettingValue } from '../../hooks/useAppSettingValue';
import { useGrammarCheck, useTranslateDraft } from '../../hooks/useAi';

/**
 * The AI writing assistant that sits above a message body: correct it, or
 * translate it.
 *
 * Standalone rather than inline in the composer because it is wanted in more
 * than one place — the email composer today, the thread reply box in Phase D —
 * and because a caller should get the enabled-check, the tone, the
 * stale-suggestion guard and the accept/dismiss flow by rendering one thing.
 *
 * ⚠️ **It never edits the draft.** The host owns the text; this asks for a
 * suggestion and applying one is an explicit click. That is what lets the same
 * component sit behind a rich-text body, a plain textarea and a description
 * field without knowing anything about any of them.
 *
 * ⚠️ **A suggestion never outlives the draft it was made for.** One edited
 * under a suggestion drops it; one edited *during* the request discards the
 * response when it lands. Both are the same failure — a suggestion for a draft
 * that has since been edited, whose "Use this" would silently throw away
 * everything typed in between — and they need separate guards because the
 * second changes the draft before there is anything on screen to clear.
 *
 * What counts as "the draft" is the host's to say, through `revision`. A
 * rich-text host strips markup to produce `text`, so an edit that changes only
 * formatting leaves `text` identical and both guards blind to it.
 *
 * ⚠️ **Our markup, not `<AiAssistPanel>`.** CLAUDE.md's rule: a screen that
 * *is* the package's renders whole, and this is a strip inside an `EntityModal`
 * on pages built here, beside our own controls. What was worth adopting is
 * behind it — the prompts, the tones and the model resolution all come from
 * `@speles7172/ai-client`.
 */
export interface WritingAssistantProps {
  /** The draft, as plain text — what the model is asked about. */
  text: string;
  /**
   * What the host considers the draft, when that is more than `text`.
   *
   * A rich-text host derives `text` by stripping markup, so an edit that
   * changes only formatting leaves `text` identical and the freshness check
   * blind to it — a suggestion requested before the edit would install and,
   * once accepted, replace the newly formatted body with flat HTML. Passing the
   * markup itself here makes "has the draft moved on?" a question about the
   * draft rather than about its shadow.
   *
   * Defaults to `text`, which is right for a plain-text host.
   */
  revision?: string;
  /** Applied when the writer accepts a suggestion. */
  onAccept: (text: string) => void;
  disabled?: boolean;
  /** Surfaced to the host, which already has somewhere to show an error. */
  onError?: (message: string) => void;
}

export function WritingAssistant({
  text,
  revision,
  onAccept,
  disabled,
  onError,
}: WritingAssistantProps) {
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [tone, setTone] = useState('');
  const [language, setLanguage] = useState('');
  const [translating, setTranslating] = useState(false);

  const grammarCheck = useGrammarCheck();
  const translate = useTranslateDraft();
  const { data: enabledSetting } = useAppSettingValue('ai_grammar_enabled');
  const enabled = enabledSetting !== false;

  // What "the draft" means for freshness: the host's own value when it has one,
  // the plain text otherwise.
  const draftKey = revision ?? text;

  /**
   * The draft as it is *now*, readable from an async callback.
   *
   * Assigned during render rather than in an effect on purpose: a response can
   * arrive before an effect for the newest draft has run, and a ref one render
   * behind would answer the question "has the draft moved on?" with the wrong
   * draft — which is the whole bug this guards.
   */
  const draftRef = useRef(draftKey);
  draftRef.current = draftKey;

  /** The draft a standing suggestion was made for, or null when there is none. */
  const suggestedFor = useRef<string | null>(null);

  // Case one: the draft is edited while a suggestion is on screen.
  useEffect(() => {
    if (suggestedFor.current !== null && suggestedFor.current !== draftKey) {
      setSuggestion(null);
      suggestedFor.current = null;
    }
  }, [draftKey]);

  /**
   * Install a suggestion, unless the draft moved on while it was in flight.
   *
   * Case two, and the effect above cannot catch it: the writer edits *during*
   * the request, so `text` changes — and the effect runs — before the response
   * lands with nothing yet on screen to clear. The suggestion then installs
   * against the older draft and stays actionable, and "Use this" silently
   * discards every edit made in between.
   */
  function install(requestedFor: string, value: string) {
    if (draftRef.current !== requestedFor) return;
    suggestedFor.current = requestedFor;
    setSuggestion(value);
  }

  if (!enabled) return null;

  const busy = grammarCheck.isPending || translating || Boolean(disabled);

  function fail(err: unknown, fallback: string) {
    onError?.(err instanceof Error ? err.message : fallback);
  }

  function accept(value: string) {
    suggestedFor.current = null;
    setSuggestion(null);
    onAccept(value);
  }

  function handleCheck() {
    if (!text.trim()) {
      onError?.('Write a message first.');
      return;
    }
    const requestedFor = draftKey;
    grammarCheck.mutate(
      { text, ...(tone ? { tone } : {}) },
      {
        onSuccess: (result) => install(requestedFor, result.corrected),
        onError: (err) => fail(err, 'The grammar assistant is unavailable.'),
      },
    );
  }

  function handleTranslate() {
    if (!text.trim()) {
      onError?.('Write a message first.');
      return;
    }
    if (!language.trim()) {
      onError?.('Name the language to translate into.');
      return;
    }
    setTranslating(true);
    const requestedFor = draftKey;
    translate.mutate(
      { text, targetLanguage: language.trim(), ...(tone ? { tone } : {}) },
      {
        onSuccess: (result) => install(requestedFor, result.translated),
        onError: (err) => fail(err, 'The translation assistant is unavailable.'),
        onSettled: () => setTranslating(false),
      },
    );
  }

  return (
    <div data-testid="writing-assistant">
      <div className="flex flex-wrap items-center justify-end gap-1.5">
        <select
          value={tone}
          onChange={(e) => setTone(e.target.value)}
          aria-label="Tone"
          className="px-1.5 py-1 text-xs border border-gray-200 rounded-md text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
          data-testid="assistant-tone"
        >
          <option value="">Default tone</option>
          {AI_TONES.map((option) => (
            <option key={option.key} value={option.key}>
              {option.label}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={handleCheck}
          disabled={busy}
          className="flex items-center gap-1 px-2 py-1 text-xs text-primary-600 border border-primary-200 rounded-md hover:bg-primary-50 disabled:opacity-50"
          data-testid="check-grammar"
        >
          <Sparkles size={13} />
          {grammarCheck.isPending ? 'Checking…' : 'Check grammar'}
        </button>

        <input
          type="text"
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          placeholder="Language"
          aria-label="Translate into"
          className="w-24 px-1.5 py-1 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          data-testid="assistant-language"
        />
        <button
          type="button"
          onClick={handleTranslate}
          disabled={busy}
          className="flex items-center gap-1 px-2 py-1 text-xs text-primary-600 border border-primary-200 rounded-md hover:bg-primary-50 disabled:opacity-50"
          data-testid="translate-draft"
        >
          <Languages size={13} />
          {translating ? 'Translating…' : 'Translate'}
        </button>
      </div>

      {suggestion !== null && (
        <div
          className="mt-2 p-3 bg-primary-50 border border-primary-200 rounded-lg"
          data-testid="grammar-suggestion"
        >
          <p className="flex items-center gap-1 text-xs font-medium text-primary-700 mb-1.5">
            <Sparkles size={13} />
            Suggested rewrite
          </p>
          <p className="text-sm text-gray-800 whitespace-pre-line">{suggestion}</p>
          <div className="flex justify-end gap-2 mt-2">
            <button
              type="button"
              onClick={() => {
                suggestedFor.current = null;
                setSuggestion(null);
              }}
              className="px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded-md"
              data-testid="dismiss-grammar"
            >
              Dismiss
            </button>
            <button
              type="button"
              onClick={() => accept(suggestion)}
              className="px-2.5 py-1 text-xs bg-primary-600 text-white rounded-md hover:bg-primary-700"
              data-testid="use-grammar"
            >
              Use this
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
